import type { TRainValueJs } from '../utils/value';

/**
 * The `automations` tool source's naming convention.
 *
 * One published automation is not one tool: the source emits a BASE tool named
 * for the automation itself, plus run-inspection companions that share its name
 * and a `__` separator —
 *
 *   <slug>                    starts a run
 *   <slug>__list_runs
 *   <slug>__get_run_status
 *   <slug>__get_run_inputs
 *   <slug>__get_run_outputs
 *
 * The agent-config pane relies on the same split to collapse ~5 rows per
 * automation into one (`AutomationsSection.groupToolsByAutomation`).
 *
 * Matched by SHAPE rather than by an enumerated list, and it has to be: the
 * slugs are the WORKSPACE's published automations, so they differ per tenant and
 * are unknowable to this repo. That is also why a target agent's chat cannot
 * name these tools in `toolRenderers` — see `SANDBOX_AGENT_CONFIG`, whose whole
 * premise is that a user-created agent has no fixed tool set. They resolve
 * through `resolveToolRenderer` instead, exactly as hermes' open `graph_*`
 * family does.
 */
const RUN_TOOL_SEPARATOR = '__';

/** The companion suffixes, i.e. everything that is not the run itself. */
const GET_RUN_STATUS_SUFFIX = 'get_run_status';
const GET_RUN_OUTPUTS_SUFFIX = 'get_run_outputs';
const GET_RUN_INPUTS_SUFFIX = 'get_run_inputs';
const LIST_RUNS_SUFFIX = 'list_runs';

const RUN_COMPANION_SUFFIXES: ReadonlySet<string> = new Set([
  GET_RUN_STATUS_SUFFIX,
  GET_RUN_OUTPUTS_SUFFIX,
  GET_RUN_INPUTS_SUFFIX,
  LIST_RUNS_SUFFIX,
]);

/**
 * Split an automation tool name into its automation slug and its suffix.
 * A name with no `__` is the base tool, so it has no suffix.
 *
 * Splits on the LAST separator: an automation slug may itself contain `__`
 * (nothing forbids it), and the suffix set is closed while the slug is not.
 */
function splitRunToolName(name: string): { slug: string; suffix?: string } {
  const index = name.lastIndexOf(RUN_TOOL_SEPARATOR);
  if (index <= 0) return { slug: name };
  const suffix = name.slice(index + RUN_TOOL_SEPARATOR.length);
  // Only a KNOWN companion suffix counts; anything else is part of the slug, so
  // an unrelated tool that merely contains `__` is not mistaken for a companion.
  if (!RUN_COMPANION_SUFFIXES.has(suffix)) return { slug: name };
  return { slug: name.slice(0, index), suffix };
}

/**
 * True for one of an automation's run-inspection companions.
 *
 * These are the calls that answer "how is it going" — mechanical polling whose
 * every step does not deserve its own card. The base tool carries the card, so
 * these render as a quiet activity row instead.
 */
export function isAutomationRunCompanionTool(
  name: string | undefined,
): boolean {
  if (name === undefined) return false;
  return splitRunToolName(name).suffix !== undefined;
}

/**
 * The automation slug a run-companion tool belongs to — used to label the
 * polling row with the automation's name rather than the raw tool name.
 * `undefined` for anything that is not a companion.
 */
/**
 * True for the companion that carries a run's OUTPUTS, as opposed to its status
 * or its run list.
 *
 * Exported as a predicate rather than as its suffix string: a caller handed the
 * pieces would have to re-join them with the separator, which is the drift this
 * module exists to prevent. The convention lives here, so the question is
 * answered here.
 */
export function isRunOutputsTool(name: string | undefined): boolean {
  if (name === undefined) return false;
  return splitRunToolName(name).suffix === GET_RUN_OUTPUTS_SUFFIX;
}

export function readAutomationSlug(
  name: string | undefined,
): string | undefined {
  if (name === undefined) return undefined;
  const { slug, suffix } = splitRunToolName(name);
  return suffix === undefined ? undefined : slug;
}

/**
 * How a run settles, as reported by an automation tool result.
 *
 * Mirrors jeeves' run states (`quill.k8s.yaml`'s `get_run` normalises the same
 * five). `AWAITING_GUIDANCE` is a pause for a human decision, not a failure.
 */
export type TAutomationRunState =
  | 'PENDING'
  | 'EXECUTING'
  | 'COMPLETED'
  | 'FAILED'
  | 'STOPPED'
  | 'AWAITING_GUIDANCE';

const TERMINAL_STATES: ReadonlySet<TAutomationRunState> =
  new Set<TAutomationRunState>(['COMPLETED', 'FAILED', 'STOPPED']);

/** Whether a state means the run has stopped moving. */
export const isTerminalRunState = (
  state: TAutomationRunState | undefined,
): boolean => state !== undefined && TERMINAL_STATES.has(state);

/**
 * Read a run state out of a tool result.
 *
 * The `automations` source is fulfilled server-side (Grimoire + Jeeves), and its
 * result envelope is not generated into this repo — so this reads defensively
 * and returns `undefined` rather than guessing when the shape is unfamiliar. A
 * card that cannot find a state still renders; it just reports the tool call's
 * own success/failure instead of the run's.
 *
 * Accepts both an already-normalised string (`"COMPLETED"`) and jeeves' oneof
 * object form (`{ completed: {...} }`), in either case case-insensitively and in
 * either snake or camel case.
 */
export function extractRunState(
  result: TRainValueJs | undefined,
): TAutomationRunState | undefined {
  if (result === undefined || result === null) return undefined;

  if (typeof result === 'string') return normaliseRunState(result);

  if (typeof result !== 'object' || Array.isArray(result)) return undefined;
  const record = result as { [key: string]: TRainValueJs };

  for (const key of ['state', 'status', 'run_state', 'runState']) {
    const value = record[key];
    if (typeof value === 'string') {
      const state = normaliseRunState(value);
      if (state) return state;
    }
    // jeeves reports state as a oneof, so the STATE IS THE KEY:
    // `{ state: { completed: {...} } }`.
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      for (const inner of Object.keys(value as object)) {
        const state = normaliseRunState(inner);
        if (state) return state;
      }
    }
  }

  // A run nested one level down (`{ run: { state: ... } }`).
  const run = record.run;
  if (run && typeof run === 'object' && !Array.isArray(run)) {
    return extractRunState(run);
  }
  return undefined;
}

/** Normalise a raw state string to one of the six known states. */
function normaliseRunState(value: string): TAutomationRunState | undefined {
  // Tolerates `RUN_STATE_AWAITING_GUIDANCE`, `awaitingGuidance`, and
  // `awaiting_guidance` alike.
  const flat = value.toUpperCase().replace(/[^A-Z]/g, '');
  if (flat.endsWith('PENDING')) return 'PENDING';
  if (flat.endsWith('EXECUTING')) return 'EXECUTING';
  if (flat.endsWith('COMPLETED')) return 'COMPLETED';
  if (flat.endsWith('FAILED')) return 'FAILED';
  if (flat.endsWith('STOPPED')) return 'STOPPED';
  if (flat.endsWith('AWAITINGGUIDANCE')) return 'AWAITING_GUIDANCE';
  return undefined;
}

/** One output a run produced: its key, and its value as display text. */
export interface IAutomationRunOutput {
  name: string;
  value: string;
}

/**
 * Pull a run's OUTPUTS out of a tool result.
 *
 * The outputs are what the run was for, and they are nested: a `get_run`-shaped
 * result carries them at `result.outputs.<key>`, not at the top level. Verified
 * against a real run in the local cluster, whose payload was
 *
 *   { state: "COMPLETED",
 *     result: { outputs: { cleaned_document: "bishop://…xlsx",
 *                          duplicates_removed_count: 1 } }, … }
 *
 * so rendering the whole result would bury two useful values under the run's
 * name, inputs, connections and code URI. A `__get_run_outputs` result instead
 * returns the outputs map directly, and both shapes have to work.
 *
 * Returns `undefined` when there is no recognisable outputs map, so the caller
 * can fall back rather than inventing structure — the envelope is produced
 * server-side and is not generated into this repo.
 */
export function extractRunOutputs(
  result: TRainValueJs | undefined,
): IAutomationRunOutput[] | undefined {
  const map = findOutputsMap(result);
  if (!map) return undefined;
  const entries = Object.entries(map);
  if (entries.length === 0) return undefined;
  return entries.map(([name, value]) => ({ name, value: toDisplay(value) }));
}

/** Locate the outputs object, whether nested under `result` or top-level. */
function findOutputsMap(
  result: TRainValueJs | undefined,
): Record<string, TRainValueJs> | undefined {
  if (!result || typeof result !== 'object' || Array.isArray(result)) {
    return undefined;
  }
  const record = result as { [key: string]: TRainValueJs };

  // `get_run` nests them one level down, under the run's `result`.
  const inner = record.result;
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    const outputs = (inner as { [key: string]: TRainValueJs }).outputs;
    if (outputs && typeof outputs === 'object' && !Array.isArray(outputs)) {
      return outputs as Record<string, TRainValueJs>;
    }
  }

  // An explicit `outputs` at the top level (`__get_run_outputs`).
  const outputs = record.outputs;
  if (outputs && typeof outputs === 'object' && !Array.isArray(outputs)) {
    return outputs as Record<string, TRainValueJs>;
  }

  // `__get_run_outputs` may return the map ITSELF. Only treat it as such when
  // nothing in it looks like run metadata — otherwise a `get_run` result with
  // no outputs would render its own envelope as though those were outputs.
  const RUN_ENVELOPE_KEYS = [
    'state',
    'name',
    'run',
    'run_name',
    'executableSteps',
    'invocationDetails',
    'error',
    'stoppedReason',
    // The nested-`result` shape this function already looks inside above. A
    // `{ result: { … } }` with no `outputs` anywhere is still an envelope, and
    // without this its metadata rendered as though it were the run's outputs.
    'result',
  ];
  if (RUN_ENVELOPE_KEYS.some(key => key in record)) return undefined;
  return record;
}

/** Render one output value as a single line of display text. */
function toDisplay(value: TRainValueJs): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
